import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { usePostHog } from "@posthog/react";
import { completeGoogleSignIn, getStoredHostSub } from "../webAuth";

// Handles the Google OAuth redirect for the host controller's sign-in.
// Exchanges the code via the account service, stores the session, then sends
// the user back to the /host page they started from.
//
// This page is also where IDENTITY IS STITCHED, and that is not incidental.
// Everything the funnel needs to answer — did the wall convert, did the trial
// start, did the welcome email land — spans an anonymous browser session and a
// server that only knows a Google `sub`. posthog.identify is what merges those
// two into one person.
//
// It used to happen only on the controller's mount, which left a hole exactly
// where it mattered: a host who signed in and then closed the tab, or bounced
// on a slow connection, was never identified, so their sign-up stayed on an
// anonymous person while `signed_up` and `trial_started` landed on a second,
// disconnected one. Doing it here means it happens the moment there is
// something to identify, before anything can interrupt.
const AuthCallback = () => {
  const navigate = useNavigate();
  const posthog = usePostHog();
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    completeGoogleSignIn()
      .then(({ returnTo, isNewUser, plan }) => {
        if (cancelled) return;

        const sub = getStoredHostSub();
        if (sub) {
          // Before the event, so the event lands on the merged person rather
          // than on the anonymous one it would otherwise create.
          posthog?.identify(sub);
          posthog?.capture("host_auth_completed", {
            is_new_user: isNewUser,
            plan_source: plan?.planSource ?? null,
          });
        }

        // Replace so the callback URL (with the one-time code) isn't left in
        // history for a back-button re-submit.
        navigate(returnTo, { replace: true });
      })
      .catch((e: unknown) => {
        if (cancelled) return;
        const message = e instanceof Error ? e.message : "sign_in_failed";
        // The one event that was missing entirely: the old code fired
        // `trial_start_clicked` before the redirect to Google and then nothing
        // at all, so every OAuth failure was invisible and the drop-off looked
        // like people simply changing their minds.
        posthog?.capture("host_auth_failed", { error: message });
        setError(message);
      });

    return () => {
      cancelled = true;
    };
  }, [navigate, posthog]);

  return (
    <div
      style={{
        minHeight: "100dvh",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
        fontFamily: "system-ui, sans-serif",
        color: "#374151",
        padding: 24,
        textAlign: "center",
      }}
    >
      {error ? (
        <>
          <strong>Sign-in failed</strong>
          <span style={{ color: "#6b7280", fontSize: 14 }}>{error}</span>
        </>
      ) : (
        <span style={{ color: "#6b7280" }}>Signing you in…</span>
      )}
    </div>
  );
};

export default AuthCallback;
